import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useLeads } from '../../context/LeadsContext';

const normalize = (str) => (str || '').toLowerCase().trim();

const AttachLeadModal = ({ open, onClose, onAttach, defaultLinkQuote = true, hasQuote = false }) => {
  const { leads } = useLeads();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [linkQuote, setLinkQuote] = useState(defaultLinkQuote);

  const filteredLeads = useMemo(() => {
    const search = normalize(searchTerm);
    if (!search) return leads;

    return leads.filter((lead) => {
      const addressString = [
        lead.address?.line1,
        lead.address?.line2,
        lead.address?.city,
        lead.address?.state,
        lead.address?.postalCode,
      ]
        .filter(Boolean)
        .join(' ') 
        .toLowerCase();

      return (
        normalize(lead.name).includes(search) ||
        normalize(lead.email).includes(search) ||
        addressString.includes(search) ||
        normalize(lead.service).includes(search)
      );
    });
  }, [leads, searchTerm]);

  const handleAttachClick = () => {
    if (!selectedLeadId) return;
    onAttach?.({ leadId: selectedLeadId, linkQuote });
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedLeadId(null);
      setLinkQuote(defaultLinkQuote);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Attach Existing Lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700" htmlFor="attach-lead-search">
              Search leads
            </label>
            <Input
              id="attach-lead-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, address, or service..."
              className="text-sm"
            />
          </div>

          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 bg-white">
            {filteredLeads.length === 0 ? (
              <div className="p-4 text-xs text-gray-500 text-center">
                {searchTerm ? 'No leads found matching your search' : 'No leads yet'}
              </div>
            ) : (
              filteredLeads.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => setSelectedLeadId(lead.id)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex flex-col gap-0.5 ${
                    selectedLeadId === lead.id ? 'bg-indigo-50' : ''
                  }`}
                >
                  <span className="font-medium text-gray-900 truncate">{lead.name || 'Untitled lead'}</span>
                  <span className="text-[11px] text-gray-500 truncate">
                    {lead.email || lead.phone || 'No contact info'}
                  </span>
                  {lead.address && (
                    <span className="text-[11px] text-gray-400 truncate">
                      {[lead.address.line1, lead.address.city, lead.address.state]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {hasQuote && (
            <label className="flex items-center gap-2 text-[11px] text-gray-600">
              <input
                type="checkbox"
                checked={linkQuote}
                onChange={(e) => setLinkQuote(e.target.checked)}
                className="h-3 w-3 rounded border-gray-300"
              />
              <span>Also link the latest quote from this conversation to the selected lead</span>
            </label>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAttachClick}
              disabled={!selectedLeadId}
            >
              Attach Lead
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttachLeadModal;
